/**
 * AgentPieChart - Generic pie chart for agent visualizations
 */

import React from 'react';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';
import { ChartContainer, type ChartConfig } from '../../ui/chart';
import { cn } from '../../../lib/utils';
import { PieChartVisualization, formatValue } from './types';

interface AgentPieChartProps {
  data: PieChartVisualization;
  className?: string;
}

const COLORS = [
  'hsl(var(--chart-1))',
  'hsl(var(--chart-2))',
  'hsl(var(--chart-3))',
  'hsl(var(--chart-4))',
  'hsl(var(--chart-5))',
  'hsl(217, 91%, 60%)',
  'hsl(280, 65%, 60%)',
  'hsl(340, 75%, 55%)',
];

const chartConfig = {
  value: {
    label: "Value",
    color: "hsl(var(--chart-1))",
  },
} satisfies ChartConfig;

export const AgentPieChart: React.FC<AgentPieChartProps> = ({ data, className }) => {
  const { title, label_field, value_field, data: chartData } = data;

  // Calculate total for percentage
  const total = chartData.reduce((sum, item) => sum + (item[value_field] || 0), 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0];
      const percentage = ((item.value / total) * 100).toFixed(1);
      return (
        <div className="bg-background border rounded-lg shadow-lg p-3 min-w-[150px]">
          <p className="font-medium text-sm mb-1">{item.name}</p>
          <p className="text-sm text-muted-foreground">
            {formatValue(item.value, 'currency')}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {percentage}% of total
          </p>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: any) => {
    return (
      <div className="flex flex-wrap justify-center gap-3 mt-2">
        {payload?.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-1.5 text-xs">
            <div
              className="w-2.5 h-2.5 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={cn("w-full", className)}>
      {title && (
        <h4 className="text-sm font-medium mb-3 text-foreground">{title}</h4>
      )}
      <ChartContainer config={chartConfig} className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey={value_field}
              nameKey={label_field}
              cx="50%"
              cy="45%"
              outerRadius={80}
              innerRadius={40}
              paddingAngle={2}
              label={({ name, percent }) =>
                percent > 0.05 ? `${(percent * 100).toFixed(0)}%` : ''
              }
              labelLine={false}
            >
              {chartData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomLegend />} />
          </PieChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
};
